import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, MenuItem, IconButton, Tooltip, Alert, TextField,
  Divider, Paper, Stack, Accordion, AccordionSummary, AccordionDetails,
  LinearProgress
} from '@mui/material';
import {
  Add, Refresh, Edit, Delete, Hub, CheckCircle, Cancel, WifiTethering,
  Speed, ExpandMore, Lock, RouterOutlined, Computer, Storage
} from '@mui/icons-material';
import api from '../../services/api';

const NODE_TYPES = [
  { value: 'kamailio',   label: 'Kamailio SBC',          icon: '🔷', desc: 'Open-source SIP SBC with LCR, routing, anti-flood' },
  { value: 'opensips',   label: 'OpenSIPS SBC',           icon: '🔶', desc: 'High-performance SIP proxy and SBC' },
  { value: 'asterisk',   label: 'Asterisk PBX/SBC',       icon: '⭐', desc: 'Asterisk PBX — connect via AMI or SIP trunk' },
  { value: 'freepbx',    label: 'FreePBX (Asterisk)',      icon: '📞', desc: 'FreePBX web GUI over Asterisk — AMI connection' },
  { value: 'freeswitch', label: 'FreeSWITCH Media Server', icon: '🎵', desc: 'Media server for IVR, conferencing, transcoding' },
  { value: 'rtpengine',  label: 'RTPengine',              icon: '📡', desc: 'RTP media proxy with SRTP, NAT traversal' },
  { value: 'rtpproxy',   label: 'RTPproxy',               icon: '📡', desc: 'RTP proxying daemon' },
  { value: 'gateway',    label: 'VoIP Gateway',           icon: '🔌', desc: 'Hardware or software VoIP gateway (Cisco, Grandstream, etc.)' },
  { value: 'lb',         label: 'Load Balancer',          icon: '⚖️',  desc: 'SIP load balancer (Dispatcher, Kamailio-LB)' },
  { value: 'media',      label: 'Media Server',           icon: '🎬', desc: 'Generic media server' },
  { value: 'sbc',        label: 'Other SBC',              icon: '🛡️',  desc: 'Generic/proprietary session border controller' },
];

const ROLE_COLORS: any = {
  kamailio: '#1565c0', opensips: '#0d47a1', asterisk: '#ff6f00', freepbx: '#e65100',
  freeswitch: '#2e7d32', rtpengine: '#6a1b9a', rtpproxy: '#4a148c',
  gateway: '#37474f', lb: '#00695c', media: '#1b5e20', sbc: '#4e342e',
};

const emptyForm: any = {
  name: '', host: '', role: 'kamailio', status: 'active', description: '',
  sip_port: 5060, transport: 'UDP', location: '', sw_version: '', is_local: false,
  esl_host: '', esl_port: 8021, esl_password: '',
  ami_host: '', ami_port: 5038, ami_user: '', ami_password: '',
};

export const SbcPage: React.FC = () => {
  const [nodes, setNodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [testing, setTesting] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, any>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sbc/nodes');
      setNodes(Array.isArray(data) ? data : data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setError(''); setDialogOpen(true); };
  const openEdit = (node: any) => { setEditing(node); setForm({ ...emptyForm, ...node }); setError(''); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      if (editing) await api.put(`/sbc/nodes/${editing.id}`, form);
      else await api.post('/sbc/nodes', form);
      setDialogOpen(false); load();
    } catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleTest = async (nodeId: string) => {
    setTesting(nodeId);
    try {
      const { data } = await api.post(`/sbc/nodes/${nodeId}/test`);
      setTestResults(prev => ({ ...prev, [nodeId]: data }));
    } catch (e: any) {
      setTestResults(prev => ({ ...prev, [nodeId]: { reachable: false, message: e.response?.data?.message || 'Test failed' } }));
    } finally { setTesting(null); }
  };

  const nodeType = (role: string) => NODE_TYPES.find(t => t.value === role) || { label: role, icon: '🔧', desc: '' };

  const localNodes = nodes.filter(n => n.is_local);
  const remoteNodes = nodes.filter(n => !n.is_local);

  const NodeCard = ({ node }: { node: any }) => {
    const type = nodeType(node.role);
    const testResult = testResults[node.id];
    return (
      <Card sx={{ mb: 0 }}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {/* Role badge */}
            <Box sx={{
              width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 24, flexShrink: 0,
              bgcolor: ROLE_COLORS[node.role] + '22', border: `1px solid ${ROLE_COLORS[node.role]}44`
            }}>
              {type.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" fontWeight={700}>{node.name}</Typography>
                {node.is_local && <Chip label="LOCAL" size="small" color="primary" sx={{ height: 18, fontSize: 10 }} />}
                <Chip label={type.label} size="small" sx={{ bgcolor: ROLE_COLORS[node.role] + '22', color: ROLE_COLORS[node.role], border: `1px solid ${ROLE_COLORS[node.role]}44`, height: 20, fontSize: 10 }} />
                <Chip label={node.status} size="small" color={node.status === 'active' ? 'success' : 'default'} sx={{ height: 20 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 3, mt: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Host:</strong> {node.host}:{node.sip_port || 5060}
                  {node.transport ? ` / ${node.transport}` : ''}
                </Typography>
                {node.location && <Typography variant="caption" color="text.secondary"><strong>Location:</strong> {node.location}</Typography>}
                {node.sw_version && <Typography variant="caption" color="text.secondary"><strong>v</strong>{node.sw_version}</Typography>}
              </Box>
              {node.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>{node.description}</Typography>
              )}
              {/* Connection details for asterisk/freeswitch */}
              {(node.role === 'freeswitch' || node.role === 'media') && node.esl_host && (
                <Typography variant="caption" color="info.main">ESL: {node.esl_host}:{node.esl_port}</Typography>
              )}
              {(node.role === 'asterisk' || node.role === 'freepbx') && node.ami_host && (
                <Typography variant="caption" color="warning.main">AMI: {node.ami_host}:{node.ami_port} ({node.ami_user})</Typography>
              )}
              {/* Test result */}
              {testResult && (
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {testResult.reachable
                    ? <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                    : <Cancel sx={{ fontSize: 14, color: 'error.main' }} />}
                  <Typography variant="caption" color={testResult.reachable ? 'success.main' : 'error.main'}>
                    {testResult.message}
                  </Typography>
                </Box>
              )}
            </Box>
            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <Tooltip title="Test Connection">
                <span>
                  <IconButton size="small" onClick={() => handleTest(node.id)} disabled={testing === node.id}>
                    {testing === node.id ? <CircularProgress size={16} /> : <WifiTethering fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openEdit(node)}><Edit fontSize="small" /></IconButton>
              </Tooltip>
              {!node.is_local && (
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => { setEditing(node); setDeleteDialogOpen(true); }}><Delete fontSize="small" /></IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
      </Card>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Hub sx={{ color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>SBC & Media Nodes</Typography>
            <Typography variant="body2" color="text.secondary">
              {nodes.length} nodes — manage Kamailio, Asterisk, FreeSWITCH, RTPengine and other VoIP infrastructure
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Node</Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : (
        <Box>
          {/* Current Deployment Banner */}
          {localNodes.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                This Server (esbc2.synergytalk.org — 46.225.128.180)
              </Typography>
              <Stack spacing={1}>
                {localNodes.map(n => <NodeCard key={n.id} node={n} />)}
              </Stack>
            </Box>
          )}

          {/* Remote Nodes */}
          {remoteNodes.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Remote Nodes
              </Typography>
              <Stack spacing={1}>
                {remoteNodes.map(n => <NodeCard key={n.id} node={n} />)}
              </Stack>
            </Box>
          )}

          {nodes.length === 0 && (
            <Card sx={{ p: 6, textAlign: 'center' }}>
              <Hub sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary" gutterBottom>No nodes configured</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ mt: 1 }}>Add First Node</Button>
            </Card>
          )}
        </Box>
      )}

      {/* Deployment Info */}
      <Accordion sx={{ mt: 3 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Deployment Details — esbc2.synergytalk.org</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {[
              { label: 'Server IP', value: '46.225.128.180' },
              { label: 'Hostname', value: 'esbc2.synergytalk.org' },
              { label: 'OS', value: 'Debian 13 (trixie)' },
              { label: 'CPU / RAM', value: '2 vCPUs / 3.7 GB' },
              { label: 'Disk', value: '38 GB SSD' },
              { label: 'SSL', value: "Let's Encrypt (auto-renews)" },
              { label: 'SIP (Kamailio)', value: '46.225.128.180:5060 UDP+TCP' },
              { label: 'SIP (FreeSWITCH)', value: '46.225.128.180:5080 UDP+TCP' },
              { label: 'RTP Media', value: 'UDP ports 20000–40000' },
              { label: 'Web UI', value: 'https://esbc2.synergytalk.org' },
              { label: 'API', value: 'https://esbc2.synergytalk.org/api' },
              { label: 'Grafana', value: 'https://esbc2.synergytalk.org/grafana' },
              { label: 'RabbitMQ', value: 'https://esbc2.synergytalk.org/rabbitmq' },
              { label: 'PostgreSQL', value: 'localhost:5432 / callessential' },
              { label: 'Redis', value: 'localhost:6379' },
              { label: 'API Login', value: 'admin@callessential.io / Admin@2024!' },
              { label: 'Grafana Login', value: 'admin / Ce@Grafana2024!' },
              { label: 'RabbitMQ Login', value: 'callessential / Ce@Rabbit2024!' },
            ].map(({ label, value }) => (
              <Grid item xs={12} sm={6} md={4} key={label}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                  <Typography variant="body2" fontFamily="monospace" fontWeight={600}>{value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Edit Node: ${editing.name}` : 'Add New Node'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            {/* Basic Info */}
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="Node Name" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} required
                placeholder="e.g. Office Asterisk, NYC SBC, UAE Gateway" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Status" value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label="Node Type" value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}>
                {NODE_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{t.icon}</span>
                      <Box>
                        <Typography variant="body2">{t.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{t.desc}</Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Description" value={form.description || ''}
                onChange={e => setForm({ ...form, description: e.target.value })} />
            </Grid>

            {/* SIP Connection */}
            <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">SIP / Network</Typography></Divider></Grid>
            <Grid item xs={12} md={5}>
              <TextField fullWidth label="Host / IP Address" value={form.host}
                onChange={e => setForm({ ...form, host: e.target.value })} required
                placeholder="192.168.1.10 or sbc.example.com" />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="number" label="SIP Port" value={form.sip_port}
                onChange={e => setForm({ ...form, sip_port: parseInt(e.target.value) || 5060 })} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField select fullWidth label="Transport" value={form.transport}
                onChange={e => setForm({ ...form, transport: e.target.value })}>
                {['UDP', 'TCP', 'TLS', 'WS', 'WSS'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label="Location / DC" value={form.location || ''}
                onChange={e => setForm({ ...form, location: e.target.value })}
                placeholder="NYC, UAE, etc." />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Software Version" value={form.sw_version || ''}
                onChange={e => setForm({ ...form, sw_version: e.target.value })}
                placeholder="e.g. 18.21.0, 6.0.1" />
            </Grid>

            {/* FreeSWITCH ESL */}
            {(form.role === 'freeswitch' || form.role === 'media') && (
              <>
                <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="success.main">FreeSWITCH ESL (Event Socket)</Typography></Divider></Grid>
                <Grid item xs={12} md={5}>
                  <TextField fullWidth label="ESL Host" value={form.esl_host || ''}
                    onChange={e => setForm({ ...form, esl_host: e.target.value })}
                    placeholder="Same as host if local" />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="ESL Port" value={form.esl_port || 8021}
                    onChange={e => setForm({ ...form, esl_port: parseInt(e.target.value) || 8021 })} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="ESL Password" type="password" value={form.esl_password || ''}
                    onChange={e => setForm({ ...form, esl_password: e.target.value })}
                    placeholder="ClueCon" />
                </Grid>
              </>
            )}

            {/* Asterisk AMI */}
            {(form.role === 'asterisk' || form.role === 'freepbx') && (
              <>
                <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="warning.main">Asterisk AMI (Manager Interface)</Typography></Divider></Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="AMI Host" value={form.ami_host || ''}
                    onChange={e => setForm({ ...form, ami_host: e.target.value })}
                    placeholder="Same as SIP host if local" />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField fullWidth type="number" label="AMI Port" value={form.ami_port || 5038}
                    onChange={e => setForm({ ...form, ami_port: parseInt(e.target.value) || 5038 })} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="AMI Username" value={form.ami_user || ''}
                    onChange={e => setForm({ ...form, ami_user: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="AMI Password" type="password" value={form.ami_password || ''}
                    onChange={e => setForm({ ...form, ami_password: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    To enable AMI on Asterisk: edit <code>/etc/asterisk/manager.conf</code> and add a manager user with read/write permissions. Then run <code>asterisk -rx "manager reload"</code>.
                  </Alert>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name || !form.host}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Save Changes' : 'Add Node'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Node</DialogTitle>
        <DialogContent>
          <Typography>Remove node <strong>{editing?.name}</strong> from the platform?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={async () => {
            try { await api.delete(`/sbc/nodes/${editing.id}`); setDeleteDialogOpen(false); load(); }
            catch (e: any) { alert(e.response?.data?.message || 'Delete failed'); }
          }}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
